import React, { useMemo, useEffect, useRef, useState } from "react";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
} from "@tanstack/react-table";
import WaterMark from "../../Components/WaterMark/WaterMark";
import { useQuery } from "@tanstack/react-query";
import { axInst } from "../../Components/Login/Service";
import { Loader } from "rsuite";
import { useSelector } from "react-redux";
import { IoIosArrowForward, IoIosArrowDown } from "react-icons/io";

const offtake_comparision_api = async (body, endpoint) => {
  try {
    const res = await axInst.post(endpoint, body);
    return res.data;
  } catch (error) {
    console.error("Error fetching offtake data:", error);
    throw error;
  }
};

const expand_offtake_comparision_api = async (body, expansionEndpoint) => {
  try {
    const res = await axInst.post(expansionEndpoint, body);
    return res.data;
  } catch (error) {
    console.error("Error fetching offtake data:", error);
    throw error;
  }
};

const ExpandedRowContent = React.memo(({ data, columns }) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <table className="border w-full table-fixed">
      <tbody className="text-gray-700 text-center">
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id} className={`border bg-[#9cbed3]`}>
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                className="border border-gray-300 text-center text-xs"
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
});

const DealerReviewComparision = React.memo(
  ({ endpoint, expansionEndpoint }) => {
    const { data, submitted } = useSelector((e) => e.filtersDealerReview);

    const [expandedRows, setExpandedRows] = useState({});
    const [expandedData, setExpandedData] = useState({});
    const [expandedLoading, setExpandedLoading] = useState({});
    const [expandedError, setExpandedError] = useState({});

    const productLineMap = {
      HCV: "HCV Trucks",
      ILMCV: "ILMCV Trucks",
      SCV: "SCV & PU",
      "CV Pass": "CV Passenger",
    };

    const formatProductLine = (value) => productLineMap[value] || value;

    const handleRowExpand = async (rowId, productLine) => {
      setExpandedRows((prev) => {
        if (prev[rowId] === productLine) {
          const newState = { ...prev };
          delete newState[rowId];
          return newState;
        }
        return { ...prev, [rowId]: productLine };
      });

      if (expandedRows[rowId] === productLine) return;
      const expansionKey = `${rowId}-${productLine}`;
      setExpandedLoading((prev) => ({ ...prev, [expansionKey]: true }));

      try {
        const expansionFilters = {
          ...data,
          ...selectedOptions,
          Product_Line: [formatProductLine(productLine)],
        };
        const expansionData = await expand_offtake_comparision_api(
          expansionFilters,
          expansionEndpoint
        );
        const processedData = processExpandedData(expansionData);

        setExpandedData((prev) => ({
          ...prev,
          [expansionKey]: processedData,
        }));

        setExpandedError((prev) => ({ ...prev, [expansionKey]: false }));
      } catch (error) {
        console.error(
          `Error fetching expanded data for ${expansionKey}:`,
          error
        );
        setExpandedError((prev) => ({ ...prev, [expansionKey]: true }));
      } finally {
        setExpandedLoading((prev) => ({ ...prev, [expansionKey]: false }));
      }
    };

    const processExpandedData = (data) => {
      if (!data || !Array.isArray(data) || data.length === 0) return [];

      const totalP1 = data.reduce((sum, row) => sum + (row.P1 || 0), 0);
      const totalP2 = data.reduce((sum, row) => sum + (row.P2 || 0), 0);

      const processedRows = data.map((row) => {
        const P1_MS =
          totalP1 > 0 ? (((row.P1 || 0) / totalP1) * 100).toFixed(2) : "0";
        const P2_MS =
          totalP2 > 0 ? (((row.P2 || 0) / totalP2) * 100).toFixed(2) : "0";
        const MS_DIFF = parseFloat(P1_MS) - parseFloat(P2_MS);

        return {
          ...row,
          P1_MS,
          P2_MS,
          MS_DIFF,
        };
      });

      // Add total row
      const totalRow = {
        id: "Total",
        P1: parseFloat(totalP1.toFixed(2)),
        P1_MS: 100,
        P2: parseFloat(totalP2.toFixed(2)),
        P2_MS: 100,
        MS_DIFF: processedRows.reduce(
          (sum, row) => sum + (row.MS_DIFF || 0),
          0
        ),
      };

      return [...processedRows, totalRow];
    };

    const [selectedOptions, setSelectedOptions] = useState({
      P1_YEAR: [],
      P1_MONTH: [],
      P2_YEAR: [],
      P2_MONTH: [],
    });

    const [isOpen, setIsOpen] = useState({
      P1_YEAR: false,
      P1_MONTH: false,
      P2_YEAR: false,
      P2_MONTH: false,
    });

    const [searchTerm, setSearchTerm] = useState({
      P1_YEAR: "",
      P1_MONTH: "",
      P2_YEAR: "",
      P2_MONTH: "",
    });

    const dropdownRefs = {
      P1_YEAR: useRef(null),
      P1_MONTH: useRef(null),
      P2_YEAR: useRef(null),
      P2_MONTH: useRef(null),
    };

    const toggleDropdown = (key) => {
      setIsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSelection = (key, option) => {
      setSelectedOptions((prev) => {
        const newSelection = prev[key].includes(option)
          ? prev[key].filter((item) => item !== option)
          : [...prev[key], option];
        return { ...prev, [key]: newSelection };
      });
    };

    const handleSelectAll = (key) => {
      const allOptions = getOptions(key);
      setSelectedOptions((prev) => ({
        ...prev,
        [key]: prev[key].length === allOptions.length ? [] : allOptions,
      }));
    };

    const fetchOptionsFilter = async () => {
      const { data } = await axInst.post(
        "dealer-review/comparision_filters",
        {}
      );
      return data;
    };

    const { data: dropdownData } = useQuery({
      queryKey: ["comparisonFilters"],
      queryFn: fetchOptionsFilter,
    });

    const getOptions = (key) => {
      return dropdownData?.[key]?.buckets?.map((item) => item.key) || [];
    };

    const filteredOptions = (key) => {
      return getOptions(key).filter((option) =>
        option.toLowerCase().includes(searchTerm[key].toLowerCase())
      );
    };

    const handleApply = () => {
      setExpandedRows({});
      setExpandedData({});
      refetch();
    };

    const handleReset = () => {
      setSelectedOptions({
        P1_YEAR: [],
        P1_MONTH: [],
        P2_YEAR: [],
        P2_MONTH: [],
      });

      setExpandedRows({});
      setExpandedData({});

      setTimeout(() => {
        refetch();
      }, 0);
    };

    const {
      data: offtake_comparision_Data,
      isFetching,
      isError,
      refetch,
    } = useQuery({
      queryKey: [endpoint, submitted],
      queryFn: () =>
        offtake_comparision_api({ ...data, ...selectedOptions }, endpoint),
      cacheTime: 0,
      staleTime: 0,
    });

    useEffect(() => {
      const handleClickOutside = (event) => {
        Object.keys(dropdownRefs).forEach((key) => {
          if (
            dropdownRefs[key].current &&
            !dropdownRefs[key].current.contains(event.target)
          ) {
            setIsOpen((prev) => ({ ...prev, [key]: false }));
          }
        });
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const totalNetOfftakeP1 = useMemo(() => {
      if (!Array.isArray(offtake_comparision_Data)) return 0;
      const total =
        offtake_comparision_Data?.reduce(
          (sum, row) => sum + (row.P1 || 0),
          0
        ) || 0;
      return parseFloat(total.toFixed(2));
    }, [offtake_comparision_Data]);

    const totalNetOfftakeP2 = useMemo(() => {
      if (!Array.isArray(offtake_comparision_Data)) return 0;
      const total =
        offtake_comparision_Data?.reduce(
          (sum, row) => sum + (row.P2 || 0),
          0
        ) || 0;
      return parseFloat(total.toFixed(2));
    }, [offtake_comparision_Data]);

    const totalMsDiff = useMemo(() => {
      if (!Array.isArray(offtake_comparision_Data)) return 0;
      return (
        offtake_comparision_Data?.reduce(
          (sum, row) => sum + (row.MS_DIFF || 0),
          0
        ) || 0
      );
    }, [offtake_comparision_Data]);

    const processedData = useMemo(() => {
      if (!offtake_comparision_Data) return [];
      if (!Array.isArray(offtake_comparision_Data)) return 0;
      return offtake_comparision_Data?.map((row) => {
        const P1_MS =
          totalNetOfftakeP1 > 0
            ? (((row.P1 || 0) / totalNetOfftakeP1) * 100).toFixed(2)
            : "0";
        const P2_MS =
          totalNetOfftakeP2 > 0
            ? (((row.P2 || 0) / totalNetOfftakeP2) * 100).toFixed(2)
            : "0";
        const MS_DIFF = parseFloat(P1_MS) - parseFloat(P2_MS);

        return {
          ...row,
          P1_MS,
          P2_MS,
          MS_DIFF,
        };
      });
    }, [offtake_comparision_Data, totalNetOfftakeP1, totalNetOfftakeP2]);

    const totalRow = useMemo(() => {
      if (!offtake_comparision_Data?.length) return null;

      return {
        id: "Total",
        P1: totalNetOfftakeP1,
        P1_MS: 100,
        P2: totalNetOfftakeP2,
        P2_MS: 100,
        MS_DIFF: totalMsDiff,
      };
    }, [
      offtake_comparision_Data,
      totalNetOfftakeP1,
      totalNetOfftakeP2,
      totalMsDiff,
    ]);

    const safeData = useMemo(
      () => (totalRow ? [...processedData, totalRow] : processedData),
      [processedData, totalRow]
    );

    const tablehead = useMemo(() => {
      if (endpoint === "dealer-review/offtake_comparision") return " Offtake ";
      if (endpoint === "dealer-review/retail_comparision") return " Retail";
      if (endpoint === "dealer-review/realization_comparision")
        return " Realization ";
      return "";
    }, [endpoint]);

    const columns = useMemo(
      () => [
        { header: "", accessorKey: "id" },
        { header: "Net".concat(tablehead, "P1"), accessorKey: "P1" },
        {
          header: "P1 Salience (%)",
          accessorKey: "P1_MS",
          cell: (info) => `${info.getValue()}%`,
        },
        { header: "Net".concat(tablehead, "P2"), accessorKey: "P2" },
        {
          header: "P2 Salience (%)",
          accessorKey: "P2_MS",
          cell: (info) => `${info.getValue()}%`,
        },
        {
          header: "Salience Diff",
          accessorKey: "MS_DIFF",
          cell: (info) => `${info.getValue()?.toFixed(2)}%`,
        },
      ],
      [tablehead]
    );

    const table = useReactTable({
      data: safeData,
      columns,
      getCoreRowModel: getCoreRowModel(),
    });

    // Render expanded content
    const renderExpandedContent = (rowId) => {
      if (!expandedRows[rowId]) return null;

      const productLine = expandedRows[rowId];
      const expansionKey = `${rowId}-${productLine}`;

      if (expandedLoading[expansionKey]) {
        return (
          <tr>
            <td colSpan={columns.length} className="p-2">
              <div className="flex justify-center items-center py-4">
                <Loader size="sm" />
              </div>
            </td>
          </tr>
        );
      }

      if (expandedError[expansionKey]) {
        return (
          <tr>
            <td colSpan={columns.length} className="p-2">
              <div className="text-center text-red-500 py-2">
                Error loading expanded data.
              </div>
            </td>
          </tr>
        );
      }

      const expandedRowData = expandedData[expansionKey];

      if (!expandedRowData || expandedRowData.length === 0) {
        return (
          <tr>
            <td colSpan={columns.length} className="p-2">
              <div className="text-center py-2">No data available.</div>
            </td>
          </tr>
        );
      }

      return (
        <tr>
          <td colSpan={columns.length} className="border border-gray-400">
            <div className="bg-gray-50">
              <ExpandedRowContent
                data={expandedRowData.filter((row) => row.id !== "Total")}
                columns={columns}
              />
            </div>
          </td>
        </tr>
      );
    };

    if (isFetching)
      return (
        <div className="grid items-center justify-center w-full h-full">
          <Loader />
        </div>
      );

    if (isError)
      return <div className="text-center h-full">Error loading data.</div>;

    if (!offtake_comparision_Data?.length)
      return <div className="text-center h-full">Data Not Available</div>;

    return (
      <div className="bg-white shadow-lg text-xs rounded-lg w-full relative ">
        <WaterMark />
        <div className="flex flex-wrap gap-4 mb-2 p-2 items-center ">
          {["P1_YEAR", "P1_MONTH", "P2_YEAR", "P2_MONTH"].map((key) => (
            <div key={key} className="relative" ref={dropdownRefs[key]}>
              <button
                className="border border-[#2c8a8b] p-1 rounded bg-white w-[100px] whitespace-nowrap"
                onClick={() => toggleDropdown(key)}
              >
                {selectedOptions[key].length > 0
                  ? `${selectedOptions[key][0]} (${selectedOptions[key].length})`
                  : `Select ${key.replace("_", " ")}`}
              </button>

              {isOpen[key] && (
                <div className="absolute top-full mt-2 left-0 bg-white shadow-lg border rounded p-2 w-40 max-h-60 overflow-y-auto z-[20]">
                  <input
                    type="text"
                    placeholder="Search..."
                    className="border p-1 mb-2 w-full"
                    value={searchTerm[key]}
                    onChange={(e) =>
                      setSearchTerm((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))
                    }
                  />
                  <label className="flex items-center gap-2 p-1 font-bold">
                    <input
                      type="checkbox"
                      checked={
                        selectedOptions[key].length === getOptions(key).length
                      }
                      onChange={() => handleSelectAll(key)}
                    />
                    Select All
                  </label>
                  {filteredOptions(key).map((option) => (
                    <label key={option} className="flex items-center gap-2 p-1">
                      <input
                        type="checkbox"
                        checked={selectedOptions[key].includes(option)}
                        onChange={() => handleSelection(key, option)}
                      />
                      {option}
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <button
              className="bg-[#2A5775] text-white px-2 py-1 rounded"
              onClick={handleApply}
            >
              Apply
            </button>
            <button
              className="bg-[#2A5775] text-white px-2 py-1 rounded"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>

        <table className="border w-full table-fixed">
          <thead className="bg-[#28526E] text-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="py-1 px-[2px] border text-center text-xs"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="text-gray-700 text-center">
            {table.getRowModel().rows.map((row) => {
              const rowId = row.original.id;
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className={`border ${
                      rowId === "Total"
                        ? "bg-[#28536F] text-white font-bold"
                        : ""
                    }`}
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => {
                      let cellClass = cellIndex === 0 ? "bg-[#D3DDE6]" : "";
                      if (rowId === "Total")
                        cellClass = "bg-[#28536F] text-white font-bold";

                      const cellValue = cell.getValue();
                      const expandableCell = [
                        "HCV",
                        "ILMCV",
                        "CV Pass",
                        "SCV",
                      ].includes(cellValue);

                      return (
                        <td
                          key={cell.id}
                          className={`py-[6px] border border-[#B9B9B9] text-center ${cellClass}`}
                        >
                          {expandableCell ? (
                            <div
                              className="flex items-center justify-between px-2 cursor-pointer"
                              onClick={() => handleRowExpand(rowId, cellValue)}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                              {expandedRows[rowId] === cellValue ? (
                                <IoIosArrowDown />
                              ) : (
                                <IoIosArrowForward />
                              )}
                            </div>
                          ) : (
                            <div>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {expandedRows[rowId] && renderExpandedContent(rowId)}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
);

export default DealerReviewComparision;
